import React, { useCallback, useState, useEffect, useContext, useRef, useMemo } from 'react';
import TextLoop from "react-text-loop";

import { useLocalizedStrings, LocalizationContext } from 'app/localization/Context';

import { DeviceList } from 'app/home/main-pages/devices/Devices';
import { EditScenarioSwitchPage, ScenarioSection } from '../../deivce-detail/device-control/ScenarioSwitchControl';
import { DeviceDetailSecondPageAnimator } from '../../deivce-detail/DeviceDetail'
import { AddDeviceStatus } from '../AddDevice';
import { AddDeviceCategory, useAddDeviceConfig } from '../Config';

import { ReactComponent as IconBack } from 'images/general/back.svg';
import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconAll } from 'images/general/all.svg';
import { ReactComponent as IconSearchRadar } from 'images/device/add-device/search-radar.svg';
import { ReactComponent as IconLoading } from 'images/connection/connecting-white.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';
import { ReactComponent as IconBackBlack } from 'images/general/back-black.svg';

import { ReactComponent as IconCurtainOpen1 } from 'images/device/status-icon/curtain-open-1.svg';
import { ReactComponent as IconCurtainOpen2 } from 'images/device/status-icon/curtain-open-2.svg';


const Categories = [
    AddDeviceCategory.TouchSwitches,
    AddDeviceCategory.SecurityDevices,
    AddDeviceCategory.TemperatureControlDevices,
    AddDeviceCategory.ElectricityControlDevices,
    AddDeviceCategory.ConvenienceDevices,
];

export function SelectDeviceCategory({ dismissHandler, onCategorySelection, negotiatedProtocol }) {
    const ad = useLocalizedStrings().addDeviceProcess;

    const { configByCaterory } = useAddDeviceConfig(negotiatedProtocol);

    return (
        <div className="add-device-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{ad.selectCategory}</div>

            <div className="device-categories">
                {
                    Categories.map(categoryId => {
                        const categoryInfo = configByCaterory[categoryId];
                        return (
                            <div
                                key={"select_category_" + String(categoryId)}
                                className="cell"
                                onClick={() => {
                                    onCategorySelection(categoryInfo);
                                }}>
                                <div className="title">{categoryInfo.name}</div>
                                <div className="line" />
                                <div className="types">{
                                    categoryInfo.types.map(typeInfo => (
                                        <div className="type">
                                            {React.createElement(typeInfo.icon, { className: "icon" })}
                                            <div className="title">{typeInfo.name}</div>
                                        </div>
                                    ))
                                }</div>
                            </div>)
                    })
                }

                <div style={{
                    height: 16,
                }} /> {/* blank space */}

            </div>
        </div>
    );
}

export function SelectDeviceType({ dismissHandler, onTypeSelection, selectedCategory }) {
    const ad = useLocalizedStrings().addDeviceProcess;

    return (
        <div className="add-device-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{ad.selectType}</div>

            <div className="device-types">
                {
                    selectedCategory.types.filter(typeInfo => typeInfo.isAvailable).map((typeInfo, index) => {
                        return (
                            <div key={"select_type_" + String(index)}
                                className="cell"
                                onClick={() => {
                                    onTypeSelection(typeInfo);
                                }}>
                                {React.createElement(typeInfo.icon, { className: "icon" })}
                                <div className="title">{typeInfo.name}</div>
                                <IconBack className="next-icon" />
                            </div>
                        );
                    })
                }

                <div style={{
                    height: 16,
                }} /> {/* blank space */}

            </div>
        </div>
    );
}

export function SelectDeviceTypeL2({ dismissHandler, onTypeSelection, selectedTypeCategory }) {
    return (
        <div className="add-device-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{selectedTypeCategory.selectPageTitle}</div>

            <div className="device-types">
                {
                    selectedTypeCategory.types.filter(typeInfo => typeInfo.isAvailable).map((typeInfo, index) => {
                        return (
                            <div key={"select_type_l2_" + String(index)}
                                className="cell"
                                onClick={() => {
                                    onTypeSelection(typeInfo);
                                }}>
                                {React.createElement(typeInfo.icon, { className: "icon" })}
                                <div className="title">{typeInfo.name}</div>
                                <IconBack className="next-icon" />
                            </div>
                        );
                    })
                }

                <div style={{
                    height: 16,
                }} /> {/* blank space */}

            </div>
        </div>
    );
}

export function ResetDeviceInstruction({ dismissHandler, selectedType, confirmDeviceReset }) {
    const ad = useLocalizedStrings().addDeviceProcess;

    return (
        <div className="add-device-page-content override-grid-for-reset-page">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{ad.resetDevicePageTitle}</div>

            <div className="reset-instruction">{selectedType.resetInstruction}</div>

            <div className="reset-image">
                {selectedType.resetImage ? React.createElement(selectedType.resetImage, { style: selectedType.customResetImageStyle, className: "reset-image-content" }) : null}
            </div>

            <div /> {/* bottom padding */}

            <div className="bottom-blue-button" onClick={confirmDeviceReset}>
                <div>{selectedType.resetInstructionConfirm}</div> <IconBackBlack className="icon-180" />
            </div>
        </div>
    );
}

export function SelectDeviceToAdd({ dismissHandler, candidateDevices, searching, selectedType, onDeviceSelection, selectAllDevices, searchDuration }) {
    const l = useContext(LocalizationContext);
    const ad = l.strings.addDeviceProcess;

    const [searchTimer, setSearchTimer] = useState(searchDuration);
    let searchIntervalTimer = useRef(undefined);

    useEffect(() => {
        setSearchTimer(searchDuration);
        if (searchDuration !== undefined) {
            let i = setInterval(() => {
                setSearchTimer(t => t - 1);
            }, 1000);
            searchIntervalTimer.current = i;
            return () => { clearInterval(i) };
        }
    }, [searchDuration]);

    useEffect(() => {
        if (searchTimer < 0)
            clearInterval(searchIntervalTimer.current);
    }, [searchTimer]);

    const validSearchTips = useMemo(() => {
        let _validSearchTips = [];
        for (let i = 0; i < ad.searchTips.length; i++) {
            const searchTip = ad.searchTips[i];
            // console.log(searchTip, selectedType.wifi, selectedType.pluggable);
            // console.log(searchTip.selector.wifi.includes(selectedType.wifi),
            //     searchTip.selector.pluggable.includes(selectedType.pluggable));
            if (
                searchTip.selector.wifi.includes(selectedType.wifi) &&
                searchTip.selector.pluggable.includes(selectedType.pluggable)
            ) {
                _validSearchTips.push(searchTip.text);
            }
        }
        return _validSearchTips;
    }, [ad.searchTips, selectedType])

    return (
        <div className="add-device-page-content override-grid-for-serach-page">
            <div className="nav-button" onClick={dismissHandler} />
            <div className="header-group">
                <IconX className="x-icon" />
                <div />

                {(candidateDevices.length > 1) ? <div className="button-border" onClick={() => selectAllDevices()}>
                    <div className="label">{ad.selectAll}</div>
                    <IconAll className="icon" />
                </div> : null}
            </div>


            <div className="header-bar">
                <div className="header">{(candidateDevices.length > 0) ? ad.selectDevice : ad.searchingDevice}</div>
                <div />
                {
                    (searching && (candidateDevices.length > 0)) ? <IconLoading className="search-icon" /> : null
                }
            </div>

            <div className="search-tip">
                <div className="header">{ad.searchTipsHeader}</div>
                <TextLoop children={validSearchTips} />
            </div>

            <div className="the-rest-of-search-page">
                {
                    (candidateDevices.length > 0) ?
                        <DeviceList
                            style={{ overflow: "auto" }} lang="en"
                            devices={candidateDevices}
                            selectionCallback={onDeviceSelection}
                            showIndicateButton={true}
                            showStates={false}
                            dontOpenSettings={true}
                            showSubDevices={false} /> :
                        <IconSearchRadar className="search-radar-icon" />
                }

                {
                    (searchDuration === undefined || searchTimer < 0 || !searching) ? null : (
                        <div className="search-bar-group">
                            <div className="search-bar" style={{
                                width: String(100 - Math.min(searchDuration, Math.max(searchTimer, 0)) / searchDuration * 100) + "%"
                            }} />
                            <div className="text">{
                                l.localizeNumber(String(Math.min(searchDuration, Math.max(searchTimer, 0))))
                                + " "
                                + ((searchTimer + 1 > 1) ? ad.searchBarText : ad.searchBarText1)}</div>
                        </div>
                    )
                }
            </div>
        </div>
    );
}

export function MultiDeviceStatus({ dismissHandler, candidateDevices }) {
    const l = useContext(LocalizationContext);
    const ad = l.strings.addDeviceProcess;

    const devicesToShow = candidateDevices.filter(d => d.addDeviceStatus !== AddDeviceStatus.notSelected);
    const showDone = devicesToShow.filter(d => d.addDeviceStatus === AddDeviceStatus.pairing).length === 0;

    return (
        <div className="add-device-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{ad.pairingStatus}</div>

            <DeviceList
                style={{ marginTop: 16, marginBottom: showDone ? 64 : undefined }} lang="en"
                devices={devicesToShow}
                showIndicateButton={true}
                showStates={false}
                dontOpenSettings={true}
                showSubDevices={false} />

            <div className="full-blue-button next-button" onClick={dismissHandler} style={{ opacity: (showDone) ? 1 : 0 }}>
                <div>{ad.finish}</div> <IconTick className="icon" />
            </div>
        </div>
    );
}

export function SelectCurtainNumChannels({ dismissHandler, onDeviceSelection, selectAllDevices, selectedDevice }) {
    const l = useContext(LocalizationContext);
    const ad = l.strings.addDeviceProcess;

    const selectNumChan1 = useCallback(() => {
        if (selectedDevice) {
            const device = { ...selectedDevice };
            device.num_channels = 1;
            onDeviceSelection(device);
        } else {
            selectAllDevices({ num_channels: 1 });
        }
    }, [selectedDevice, selectAllDevices, onDeviceSelection]);

    const selectNumChan2 = useCallback(() => {
        if (selectedDevice) {
            const device = { ...selectedDevice };
            device.num_channels = 2;
            onDeviceSelection(device);
        } else {
            selectAllDevices({ num_channels: 2 });
        }
    }, [selectedDevice, selectAllDevices, onDeviceSelection]);

    return (
        <div className="add-device-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{ad.curtainSelectNumChannelsPageTitle}</div>

            <div className="curtain-channels-list">
                <div className="box" onClick={selectNumChan1}>
                    <div className="icon-box" dir="ltr">
                        <IconCurtainOpen1 className="icon" />
                    </div>
                    <div className="title">{ad.curtainOneChannel}</div>
                </div>
                <div className="box" onClick={selectNumChan2}>
                    <div className="icon-box" dir="ltr">
                        <IconCurtainOpen1 className="icon" />
                        <IconCurtainOpen2 className="icon" />
                    </div>
                    <div className="title">{ad.curtainTwoChannels}</div>
                </div>
            </div>
        </div>
    );
}

export function ChooseDeviceName({ dismissHandler, onNameSpecified, currentDeviceName }) {
    const ad = useLocalizedStrings().addDeviceProcess;

    const [name, setName] = useState("");
    const nextClick = useCallback(() => {
        if (name.length > 1) {
            onNameSpecified(name);
            dismissHandler();
        }
    }, [name, onNameSpecified, dismissHandler]);

    useEffect(() => {
        setName(currentDeviceName)
    }, [currentDeviceName]);

    return (
        <div className="add-device-page-content">
            {/* <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} /> */}

            <div className="header" style={{ marginTop: 40 }}>{ad.enterName}</div>
            <div className="info">{ad.deviceAddedInfo}</div>

            <div className="input-header-label for-device-name">{ad.name}</div>
            <div className="input-container">
                <input className="input"
                    placeholder={currentDeviceName}
                    value={name}
                    onChange={(e) => { setName(e.target.value) }}></input>
            </div>

            {!(name.length > 1) ? <div className="error-label">{ad.youMustEnterAName}</div> : null}
            <div className="bottom-blue-button" onClick={nextClick} style={{ opacity: (name.length > 1) ? 1 : 0.3 }}>
                <div>{ad.setName}</div> <IconTick className="icon" />
            </div>
        </div>
    );
}

export function ChooseScenarioSwitchConfig({
    dismissHandler, onNameSpecified, currentDeviceName,
    gwDevice, scenarios }) {
    const s = useLocalizedStrings();
    const ad = s.addDeviceProcess;

    const [name, setName] = useState("");
    const nextClick = useCallback(() => {
        if (name.length > 1) {
            onNameSpecified(name);
            dismissHandler();
        }
    }, [name, onNameSpecified, dismissHandler]);

    useEffect(() => {
        setName(currentDeviceName)
    }, [currentDeviceName]);

    const [sectionEditShape, setSectionEditShape] = useState(undefined);

    return (
        <div className="add-device-page-content scenario-switch-config">
            {/* <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} /> */}

            <div className="header" style={{ marginTop: 40 }}>{ad.setScenarioSwitchConfigTitle}</div>
            <div className="info">{ad.deviceAddedInfo}</div>

            <div className="input-header-label for-device-name">{ad.name}</div>
            <div className="input-container">
                <input className="input"
                    placeholder={currentDeviceName}
                    value={name}
                    onChange={(e) => { setName(e.target.value) }}></input>
            </div>

            {
                (gwDevice === undefined) ? null :
                    <div className="scenario-switch-detail-control">
                        <ScenarioSection shape="o" device={gwDevice}
                            onEditClick={() => setSectionEditShape("o")} />
                        <ScenarioSection shape="x" device={gwDevice}
                            onEditClick={() => setSectionEditShape("x")} />
                        <ScenarioSection shape="t" device={gwDevice}
                            onEditClick={() => setSectionEditShape("t")} />
                        <ScenarioSection shape="r" device={gwDevice}
                            onEditClick={() => setSectionEditShape("r")} />

                        <div style={{ height: 16 + 64 }} /> {/* blank space */}

                        <DeviceDetailSecondPageAnimator isActive={sectionEditShape !== undefined}>
                            <EditScenarioSwitchPage
                                scenarios={scenarios}
                                device={gwDevice}
                                sectionEditShape={sectionEditShape}
                                setSectionEditShape={setSectionEditShape} />
                        </DeviceDetailSecondPageAnimator>
                    </div>
            }

            {!(name.length > 1) ? <div className="error-label-for-full-button">{ad.youMustEnterAName}</div> : null}
            {/* vvvv in CreateScenario.scss vvvv */}
            <div className="full-blue-button next-button" onClick={nextClick} style={{ opacity: (name.length > 1) ? 1 : 0.3 }}>
                <div>{s.save}</div> <IconTick className="icon" />
            </div>
        </div>
    );
}